import { ExpressionSpecification } from 'maplibre-gl'
import { MapSignsIcons } from 'src/app/core/enums/map-icons.enum'

export const availableSignTypes: string[] = Object.values(MapSignsIcons)
export const signTypesPropertyFilterKey = 'mainTypeIn'

function parsePropertyFilterValue(value: string): boolean | number | string {
  const trimmedValue = value.trim()

  if (trimmedValue === 'true') {
    return true
  }
  if (trimmedValue === 'false') {
    return false
  }

  // Only parse plain decimal numbers; keep everything else as string.
  if (/^-?\d+(\.\d+)?$/.test(trimmedValue)) {
    return Number(trimmedValue)
  }

  return value
}

function withPropertyFilters(
  baseFilter: ExpressionSpecification,
  propertyFilters: Record<string, string>,
): ExpressionSpecification {
  const propertyExpressions = Object.entries(propertyFilters)
    .filter(([, value]) => value !== undefined && value !== '')
    .map(([key, value]) => {
      if (key.endsWith('Contains')) {
        const propertyName = key.slice(0, -'Contains'.length)

        return [
          '>=',
          ['index-of', value, ['to-string', ['get', propertyName]]],
          0,
        ] as ExpressionSpecification
      }

      if (key.endsWith('In')) {
        const propertyName = key.slice(0, -2)
        const values = value
          .split('|')
          .map((entry) => entry.trim())
          .filter((entry) => entry.length > 0)
          .map(parsePropertyFilterValue)

        return ['in', ['get', propertyName], ['literal', values]] as ExpressionSpecification
      }

      return ['==', ['get', key], parsePropertyFilterValue(value)] as ExpressionSpecification
    })

  if (!propertyExpressions.length) {
    return baseFilter
  }

  return ['all', baseFilter, ...propertyExpressions]
}

export function normalizeSelectedSignTypes(selectedSignTypes: string[]): string[] {
  if (!selectedSignTypes.length) {
    return [...availableSignTypes]
  }

  return selectedSignTypes.filter((signType, index) => {
    return availableSignTypes.includes(signType) && selectedSignTypes.indexOf(signType) === index
  })
}

export function withSignTypeFilter(
  baseFilter: ExpressionSpecification,
  selectedSignTypes: string[],
  propertyFilters: Record<string, string> = {},
): ExpressionSpecification {
  const normalizedSignTypes = normalizeSelectedSignTypes(selectedSignTypes)
  const signTypesValue = normalizedSignTypes.join('|')

  // Keep existing explicit propertyFilters value if present, otherwise derive it
  // from selectedSignTypes to preserve current behavior at call sites.
  const mergedPropertyFilters: Record<string, string> = {
    ...propertyFilters,
  }

  if (!mergedPropertyFilters[signTypesPropertyFilterKey]) {
    mergedPropertyFilters[signTypesPropertyFilterKey] = signTypesValue
  }

  return withPropertyFilters(baseFilter, mergedPropertyFilters)
}
